import axios from "axios";
import { authHeader } from "./auth";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
// THIS BREAKS THE AUTH. `REACT_APP_BACKEND_URL` is set at build time.
const BASE = process.env.REACT_APP_BACKEND_URL;
export const API_ROOT = `${BASE}/api/v1`;

export const api = axios.create({
  baseURL: API_ROOT,
  timeout: 20000,
});

api.interceptors.request.use((cfg) => {
  const h = authHeader();
  if (h.Authorization) {
    cfg.headers = { ...cfg.headers, ...h };
  }
  return cfg;
});

// --- Catalog ---------------------------------------------------------

export async function listSets() {
  const { data } = await api.get("/tcgs/pokemon/languages/en/sets");
  return data;
}
export async function getSet(externalId) {
  const { data } = await api.get(`/sets/${externalId}`);
  return data;
}
export async function getSetBySlug(slug) {
  const { data } = await api.get(`/sets/by-slug/${encodeURIComponent(slug)}`);
  return data;
}
export async function getCardInSetByNumber(setExternalId, number) {
  const { data } = await api.get(
    `/sets/${setExternalId}/cards/by-number/${encodeURIComponent(number)}`,
  );
  return data;
}
export async function listCardsInSet(externalId, { page = 1, pageSize = 60 } = {}) {
  const { data } = await api.get(`/sets/${externalId}/cards`, {
    params: { page, page_size: pageSize },
  });
  return data;
}
export async function getCard(externalId) {
  const { data } = await api.get(`/cards/${externalId}`);
  return data;
}
export async function getCardVariant(externalId, variantSlug) {
  const { data } = await api.get(`/cards/${externalId}/${variantSlug}`);
  return data;
}
export async function listCardVariants(externalId) {
  const { data } = await api.get(`/cards/${externalId}/variants`);
  return data;
}
export async function searchCards(q, { page = 1, pageSize = 30 } = {}) {
  const { data } = await api.get("/search", {
    params: { q, page, page_size: pageSize },
  });
  return data;
}

// --- Auth ------------------------------------------------------------

export async function loginWithGoogle(credential) {
  const { data } = await api.post("/auth/google", { credential });
  return data;
}
export async function testLogin(role) {
  const { data } = await api.post("/test/login", { role });
  return data;
}
export async function fetchMe() {
  const { data } = await api.get("/me");
  return data.user;
}
export async function fetchPublicProfile(userId) {
  const { data } = await api.get(`/users/${userId}`);
  return data;
}
export async function setMyUsername(username) {
  const { data } = await api.put("/me/username", { username });
  return data.user;
}

// --- Submissions -----------------------------------------------------

export async function presignSubmissionUpload(payload) {
  // { card_external_id, kind, content_type, size }
  const { data } = await api.post("/submissions/presign", payload);
  return data;
}

export async function finalizeSubmission(payload) {
  const { data } = await api.post("/submissions/finalize", payload);
  return data.submission;
}

export async function getMyCardSubmission(cardExternalId, variantSlug) {
  const { data } = await api.get(
    `/cards/${cardExternalId}/${variantSlug}/submissions/mine`,
  );
  return data.submission;
}

export async function listMySubmissions(statusFilter) {
  const params = statusFilter ? { status: statusFilter } : undefined;
  const { data } = await api.get("/submissions/mine", { params });
  return data;
}

// --- Showcases + voting (Phase 4) --------------------------------------

export async function listCardShowcases(cardExternalId, variantSlug, { page = 1 } = {}) {
  const { data } = await api.get(
    `/cards/${cardExternalId}/${variantSlug}/showcases`,
    { params: { page } },
  );
  return data;
}

export async function listUserShowcases(userId, { page = 1, pageSize = 24 } = {}) {
  const { data } = await api.get(`/users/${userId}/showcases`, {
    params: { page, page_size: pageSize },
  });
  return data;
}

export async function getShowcase(submissionId) {
  const { data } = await api.get(`/showcases/${submissionId}`);
  return data;
}

export async function fetchLeaderboard({ tcgId = "pokemon", page = 1, pageSize = 30 } = {}) {
  const { data } = await api.get("/leaderboard", {
    params: { tcg_id: tcgId, page, page_size: pageSize },
  });
  return data;
}

export async function castVote(submissionId) {
  const { data } = await api.post(`/submissions/${submissionId}/vote`);
  return data;
}

export async function removeVote(submissionId) {
  const { data } = await api.delete(`/submissions/${submissionId}/vote`);
  return data;
}

// --- Admin (Phase 5) --------------------------------------------------

export async function listMappings({ status, q } = {}) {
  const params = {};
  if (status) params.status = status;
  if (q) params.q = q;
  const { data } = await api.get("/admin/mappings", { params });
  return data;
}
export async function confirmMapping(setExternalId, groupId, groupName) {
  const { data } = await api.post(`/admin/mappings/${setExternalId}/confirm`, {
    tcgplayer_group_id: String(groupId),
    tcgplayer_group_name: groupName || null,
  });
  return data;
}
export async function markNoMatch(setExternalId) {
  const { data } = await api.post(`/admin/mappings/${setExternalId}/no-match`);
  return data;
}
export async function suggestMappingsAll() {
  const { data } = await api.post("/admin/mappings/suggest");
  return data;
}
export async function searchTcgplayerGroups(q) {
  const { data } = await api.get("/admin/tcgplayer/groups", { params: { q } });
  return data.groups;
}
export async function triggerPriceRefresh() {
  const { data } = await api.post("/admin/prices/refresh");
  return data;
}
export async function triggerPriceRefreshSet(setExternalId) {
  const { data } = await api.post(`/admin/prices/refresh-set/${setExternalId}`);
  return data;
}
export async function priceStatus() {
  const { data } = await api.get("/admin/prices/status");
  return data;
}
export async function setSubstitutePrice(cardExternalId, variantSlug, valueUsd) {
  const { data } = await api.post(
    `/admin/cards/${cardExternalId}/${variantSlug}/substitute-price`,
    { value_usd: valueUsd },
  );
  return data;
}
export async function moderationQueue({ status = "pending", page = 1, pageSize = 30 } = {}) {
  const { data } = await api.get("/admin/moderation/queue", {
    params: { status, page, page_size: pageSize },
  });
  return data;
}
export async function moderateSubmission(subId, status, note) {
  const body = { status };
  if (note) body.note = note;
  const { data } = await api.post(
    `/admin/submissions/${subId}/moderate`,
    body,
  );
  return data.submission;
}
export async function requeueVideo(subId) {
  const { data } = await api.post(`/admin/videos/requeue/${subId}`);
  return data;
}
export async function adminCacheStatus() {
  const { data } = await api.get("/admin/catalog/cache-status");
  return data;
}
export async function adminRarityStatus() {
  const { data } = await api.get("/admin/catalog/rarity-status");
  return data;
}
export async function adminCatalogStatus() {
  const { data } = await api.get("/admin/catalog/sync-status");
  return data;
}
export async function triggerCatalogSync() {
  const { data } = await api.post("/admin/sync-catalog");
  return data;
}
export async function triggerImageCache() {
  const { data } = await api.post("/admin/cache-images");
  return data;
}
export async function resetCacheErrors() {
  const { data } = await api.post("/admin/cache-images/reset-errors");
  return data;
}
export async function triggerRarityEnrich() {
  const { data } = await api.post("/admin/enrich-rarity");
  return data;
}
export async function presignReferenceUpload(cardExternalId, contentType, size) {
  const { data } = await api.post(
    `/admin/cards/${cardExternalId}/reference/presign`,
    { content_type: contentType, size },
  );
  return data;
}
export async function finalizeReferenceUpload(cardExternalId, key) {
  const { data } = await api.post(
    `/admin/cards/${cardExternalId}/reference/finalize`,
    { key },
  );
  return data;
}
export async function clearReferenceOverride(cardExternalId) {
  const { data } = await api.post(
    `/admin/cards/${cardExternalId}/reference/clear-override`,
  );
  return data;
}

/** Direct PUT to a presigned R2 URL. Returns the ETag. */
export async function putToR2(putUrl, blob, contentType, onProgress) {
  // NOT via the axios client (no baseURL, no auth header, and R2 rejects
  // extra headers). Use fetch to keep it minimal.
  const xhr = new XMLHttpRequest();
  return new Promise((resolve, reject) => {
    xhr.open("PUT", putUrl, true);
    xhr.setRequestHeader("Content-Type", contentType);
    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress(e.loaded / e.total);
      };
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve(xhr.getResponseHeader("ETag"));
      else reject(new Error(`R2 PUT failed (${xhr.status})`));
    };
    xhr.onerror = () => reject(new Error("R2 PUT network error"));
    xhr.send(blob);
  });
}
